import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { startTicketImport } from '../../api/endpoints/postSale';
import type { AirBooking } from '../../api/types/air';
import type { Passenger } from '../../api/types/common';

/**
 * "Bilet İçe Aktarma" — sits at the very bottom of every Air ticket-detail page. For each
 * `AirBooking` in the finalized cart × each `PassengerBreakdown.TicketNumber`, offers an
 * "İçe Aktar" button that re-triggers the Arkman ticket-import routine
 * (`arkman/start-ticket-import`, `startTicketImport`). FinalizeShopping already schedules
 * this as a Hangfire job on issue; this is the manual retry when that job didn't land.
 * Response is envelope-only, so success/failure is a toast.
 */

interface ImportRow {
  bookingId: string;
  bookingCode?: string;
  ticketNumber: string;
  paxName?: string;
}

function buildRows(airBookings: AirBooking[], passengers: Passenger[]): ImportRow[] {
  const paxName = (id?: string): string | undefined => {
    const p = passengers.find((x) => x.paxReferanceId && x.paxReferanceId === id);
    return p ? [p.firstName, p.lastName].filter(Boolean).join(' ') || undefined : undefined;
  };
  const rows: ImportRow[] = [];
  for (const b of airBookings) {
    if (!b.id) continue;
    for (const pb of b.passengerBreakdowns ?? []) {
      if (!pb.ticketNumber) continue;
      rows.push({
        bookingId: b.id,
        bookingCode: b.bookingCode,
        ticketNumber: pb.ticketNumber,
        paxName: paxName(pb.paxReference?.paxReferanceId),
      });
    }
  }
  return rows;
}

export function TicketImportSection({
  shoppingCartId,
  airBookings,
  passengers = [],
}: {
  shoppingCartId: string;
  airBookings: AirBooking[];
  passengers?: Passenger[];
}) {
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (row: ImportRow) =>
      startTicketImport({
        shoppingCartId,
        airBookingId: row.bookingId,
        ticketNumber: row.ticketNumber,
      }),
    onSuccess: (_res, row) =>
      enqueueSnackbar(`${row.ticketNumber} için içe aktarma başlatıldı.`, { variant: 'success' }),
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'İçe aktarma başlatılamadı.', { variant: 'error' }),
  });

  const rows = buildRows(airBookings, passengers);
  if (rows.length === 0) return null;

  const pendingTicket = mutation.isPending ? mutation.variables?.ticketNumber : undefined;

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Bilet İçe Aktarma
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Arkman bilet aktarımını yeniden tetikler (biletleme sırasında zaten planlanır — bu manuel tekrardır).
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PNR</TableCell>
                <TableCell>Yolcu</TableCell>
                <TableCell>Bilet No</TableCell>
                <TableCell align="right">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.bookingId}-${row.ticketNumber}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.bookingCode ?? '—'}</TableCell>
                  <TableCell>{row.paxName ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.ticketNumber}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<CloudSyncOutlinedIcon />}
                      disabled={mutation.isPending}
                      onClick={() => mutation.mutate(row)}
                    >
                      {pendingTicket === row.ticketNumber ? 'Aktarılıyor…' : 'İçe Aktar'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
}
